import { useEffect, useId, useRef, useState } from 'react';
import { songsApi } from '@/api/songs';
import type { SetlistItem } from '@/types/setlist';
import type { Song } from '@/types/song';
import { isChordLine, tokeniseLine } from '@/utils/parseChords';
import { semitonesFromTo, transposeChordToken, transposeKey } from '@/utils/transposeChords';
import { formatSongKey } from '@/utils/songKey';
import { CHORD_SHEET_PALETTES } from '@/utils/chordSheetTheme';
import { useThemeStore } from '@/stores/themeStore';

interface Props {
  ministryId: string;
  item: SetlistItem;
  onClose: () => void;
}

const SECTION_LABELS: Record<string, string> = {
  INTRO: 'Intro',
  VERSE: 'Verso',
  PRE_CHORUS: 'Pré-refrão',
  CHORUS: 'Refrão',
  BRIDGE: 'Ponte',
  OUTRO: 'Final',
  INSTRUMENTAL: 'Instrumental',
  INTERLUDE: 'Interlúdio',
  TAG: 'Tag',
};

function renderSectionContent(content: string, semitones: number, sheetPalette: (typeof CHORD_SHEET_PALETTES)['dark']) {
  return content.split('\n').map((line, lineIndex) => {
    const chordLine = isChordLine(line);
    return (
      <div key={lineIndex} className="whitespace-pre-wrap break-words">
        {tokeniseLine(line).map((token, tokenIndex) => {
          if (token.kind === 'annotation') {
            return (
              <span
                key={tokenIndex}
                className="mx-0.5 rounded px-1.5 py-0.5 text-xs font-medium"
                style={{ backgroundColor: sheetPalette.annotationBackground, color: sheetPalette.annotationText }}
              >
                {'{' + token.text + '}' }
              </span>
            );
          }
          if (token.kind === 'chord' && chordLine) {
            return (
              <span key={tokenIndex} className="font-bold" style={{ color: sheetPalette.chord }}>
                {transposeChordToken(token.text, semitones)}
              </span>
            );
          }
          return <span key={tokenIndex}>{token.text}</span>;
        })}
      </div>
    );
  });
}

export default function SetlistSongViewer({ ministryId, item, onClose }: Props) {
  const mode = useThemeStore((state) => state.mode);
  const sheetPalette = CHORD_SHEET_PALETTES[mode];
  const [song, setSong] = useState<Song | null>(null);
  const [currentKey, setCurrentKey] = useState('');
  const [capoFret, setCapoFret] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    previousFocusRef.current = document.activeElement as HTMLElement | null;
    closeButtonRef.current?.focus();
    return () => previousFocusRef.current?.focus();
  }, []);

  useEffect(() => {
    let active = true;
    songsApi.get(ministryId, item.song_id)
      .then(({ data }) => {
        if (!active) return;
        setSong(data);
        setCurrentKey(item.key_override || data.key || 'C');
      })
      .catch(() => {
        if (active) setError('Não foi possível carregar a cifra.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [item.key_override, item.song_id, ministryId]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      onClose();
      return;
    }
    if (event.key !== 'Tab') return;

    const focusable = Array.from(
      dialogRef.current?.querySelectorAll<HTMLElement>('button:not([disabled]), select:not([disabled])') ?? [],
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const sections = song?.latest_version?.sections ?? [];
  const semitones = song ? semitonesFromTo(song.key || 'C', currentKey || song.key || 'C') : 0;
  const chordSemitones = semitones - capoFret;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-3 sm:p-6"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={handleKeyDown}
        className="flex max-h-[94vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl border border-white/10 bg-[#0A0A0F] shadow-2xl"
      >
        <header className="flex shrink-0 items-center gap-3 border-b border-white/10 px-4 py-3 sm:px-6">
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-medium uppercase tracking-wider text-[#E8724F]">Visualização do setlist</p>
            <h2 id={titleId} className="truncate text-base font-semibold text-white sm:text-lg">
              {song?.title ?? item.song_title}
            </h2>
            <p className="truncate text-xs text-white/45">{song?.artist ?? item.song_artist}</p>
          </div>
          {song && (
            <>
              <div className="flex shrink-0 items-center rounded-lg border border-[#E8724F]/30 bg-[#E8724F]/10 text-[#E8724F]">
                <button
                  type="button"
                  aria-label="Baixar tom"
                  onClick={() => setCurrentKey((key) => transposeKey(key || song.key || 'C', -1))}
                  className="flex h-9 w-9 items-center justify-center rounded-l-lg hover:bg-[#E8724F]/10"
                >
                  <i className="ri-subtract-line" aria-hidden="true" />
                </button>
                <span className="min-w-10 text-center text-sm font-bold" aria-live="polite">
                  {formatSongKey(currentKey || song.key || 'C', song.mode)}
                </span>
                <button
                  type="button"
                  aria-label="Subir tom"
                  onClick={() => setCurrentKey((key) => transposeKey(key || song.key || 'C', 1))}
                  className="flex h-9 w-9 items-center justify-center rounded-r-lg hover:bg-[#E8724F]/10"
                >
                  <i className="ri-add-line" aria-hidden="true" />
                </button>
              </div>
              <label className="flex shrink-0 items-center gap-1.5 text-xs text-white/60">
                Capo
                <select
                  aria-label="Capotraste"
                  value={capoFret}
                  onChange={(event) => setCapoFret(Number(event.target.value))}
                  className="h-9 rounded-md border border-white/15 bg-[#15151C] px-2 text-sm text-white"
                >
                  <option value={0}>Sem capo</option>
                  {Array.from({ length: 12 }, (_, index) => (
                    <option key={index + 1} value={index + 1}>{index + 1}ª casa</option>
                  ))}
                </select>
              </label>
            </>
          )}
          <button
            ref={closeButtonRef}
            type="button"
            aria-label="Fechar visualização"
            onClick={onClose}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-white/50 hover:bg-white/10 hover:text-white"
          >
            <i className="ri-close-line text-lg" aria-hidden="true" />
          </button>
        </header>

        <main className="overflow-y-auto px-5 py-6 sm:px-10 sm:py-8" style={{ backgroundColor: sheetPalette.background, color: sheetPalette.text }}>
          {loading && <p className="py-12 text-center text-sm" style={{ color: sheetPalette.muted }}>Carregando cifra...</p>}
          {!loading && error && <p className="py-12 text-center text-sm text-red-400">{error}</p>}
          {!loading && !error && sections.length === 0 && (
            <p className="py-12 text-center text-sm" style={{ color: sheetPalette.muted }}>Esta música ainda não possui cifra.</p>
          )}
          {!loading && !error && sections.length > 0 && (
            <div className="mx-auto flex max-w-3xl flex-col gap-7">
              {sections.map((section) => (
                <section key={section.id}>
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider" style={{ color: sheetPalette.muted }}>
                    {section.title || SECTION_LABELS[section.type] || section.type}
                  </h3>
                  <div className="whitespace-pre-wrap font-mono text-sm leading-8 sm:text-base">
                    {renderSectionContent(section.content, chordSemitones, sheetPalette)}
                  </div>
                </section>
              ))}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}